
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useState, useEffect } from 'react';
import {
  MdLocalShipping,
  MdLocationOn,
  MdCalendarToday,
  MdAccessTime,
  MdCheckCircle
} from "react-icons/md";
import NavBar from '../../../../component/b2c/navBar/navBar';
import Select from "react-dropdown-select";
import Sidebar from '../../../../component/b2c/sidebar/sidebar';
import { useMyContext } from '../../../../context/MyContext';

import { Base_Url, pickupRequest, Warehouselist } from '../../../../config/config';
import { toast } from 'react-toastify';
import Spinner from '../../../../component/spinner/spinner';
import '../../../../css/custom-scrollbar.css';

const schema = z.object({
  pickupLocation: z.string().optional(),
  date: z.string().min(1, { message: "Date is required" }),
  pickupTiming: z.string()
    .min(1, { message: "Pickup timing is required" })
    .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, { message: "Invalid time format, must be HH:MM" }),
});

export default function PickupRequest() {
  const [wareHouseList, setWareHouseList] = useState([]);
  const { isDarkMode, isToggleSidebar } = useMyContext();
  const [wareHouseId, setWareHouseId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: zodResolver(schema)
  });

  useEffect(() => {
    fetchWareHouseList();
  }, []);

  const fetchWareHouseList = async () => {
    const token = localStorage.getItem('token');
    const cusid = localStorage.getItem('cus_id');

    try {
      const response = await fetch(Base_Url + Warehouselist, {
        method: 'GET',
        headers: {
          'token': token,
          'cus_id': cusid,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      const transformedData = data.data.map(warehouse => ({
        value: warehouse.id,
        label: warehouse.name
      }));
      setWareHouseList(transformedData);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSelectChange = (selected) => {
    if (selected.length > 0) {
      setWareHouseId(selected[0].value);
    }
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    const currentDate = new Date();
    const selectedDate = new Date(data.date);

    const selectedTimeParts = data.pickupTiming.split(':');
    const selectedTime = new Date();
    selectedTime.setHours(selectedTimeParts[0], selectedTimeParts[1]);

    if (
      selectedDate.setHours(0, 0, 0, 0) === currentDate.setHours(0, 0, 0, 0) &&
      selectedTime.getHours() >= 13
    ) {
      toast.error('Cannot request pickup same day after 1 PM');
      setIsSubmitting(false);
      return;
    }

    if (selectedDate.setHours(0, 0, 0, 0) < currentDate.setHours(0, 0, 0, 0)) {
      toast.error('Cannot request pickup for past dates');
      setIsSubmitting(false);
      return;
    }

    const token = localStorage.getItem('token');
    const cusid = localStorage.getItem('cus_id');
    const payload = {
      warehouse_id: wareHouseId,
      order_date: data.date,
      time: data.pickupTiming
    };

    try {
      const response = await fetch(Base_Url + pickupRequest, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'token': token,
          'cus_id': cusid,
        },
        body: JSON.stringify(payload)
      });

      const responseData = await response.json();
      if (responseData.status === "SUCCESS") {
        toast.success("Pickup Request successful");
        reset();
      } else {
        toast.error(responseData.message.prepaid || responseData.message.error || responseData.message.pickup_time || responseData.message.detail || responseData.message.pickup_location);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
      <div className="z-10">
        <NavBar />
      </div>
      <div className="flex flex-1 overflow-hidden">
        {isToggleSidebar && (
          <div className="fixed z-20 h-full w-64 md:relative duration-300 bg-gray-200 dark:bg-darkmodeColor dark:text-white md:bg-transparent mt-4 md:w-64 md:z-20"
            style={window.innerWidth < 768 ? { top: "113px", height: "calc(100vh - 115px)", overflowY: 'auto' } : {}}>
            <Sidebar />
          </div>
        )}

        <div className="container mx-auto px-4 py-8 flex flex-col custom-scrollbar md:flex-row space-y-6 md:space-y-0 md:space-x-8">
          {/* Pickup Request Form */}
          <div className="w-full md:w-1/2 bg-white dark:bg-darkmodeColor  overflow-y-auto rounded-custom shadow-md custom-scrollbar dark:shadow-custom-dark overflow-hidden transform transition-all hover:scale-[1.02]">
            <div className="p-8">
              <div className="flex items-center mb-6 bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 rounded-lg">
                <MdLocalShipping className="mr-3 text-2xl" />
                <h2 className="text-2xl font-bold">Create Pickup Request</h2>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <label className="flex items-center text-sm font-medium mb-2  dark:bg-darkmodeColortext-gray-700 dark:text-gray-300">
                    <MdLocationOn className="mr-2 text-purple-600" />
                    Pickup Location
                  </label>
                  <Select
                    options={wareHouseList}
                    onChange={handleSelectChange}
                    className="w-full dark:bg-darkmodeColor"
                  />
                  {errors.pickupLocation && <p className="text-red-500 text-xs mt-1">{errors.pickupLocation.message}</p>}
                </div>

                <div>
                  <label className="flex items-center text-smdark:bg-darkmodeColor font-medium mb-2 text-gray-700 dark:text-gray-300">
                    <MdCalendarToday className="mr-2 text-purple-600 dark:bg-darkmodeColor" />
                    Select Date
                  </label>
                  <input
                    type="date"
                    {...register('date')}
                    className="w-full p-3 border dark:bg-darkmodeColor border-gray-300 rounded-custom focus:ring-2 focus:ring-purple-500 transition-all"
                  />
                  {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date.message}</p>}
                </div>
                <div>
                  <label className="flex items-center dark:bg-darkmodeColor text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    <MdAccessTime className="mr-2 text-purple-600" />
                    Pickup Timing
                  </label>
                  <input
                    type="time"
                    {...register('pickupTiming')}
                    className="w-full p-3 border border-gray-300 dark:bg-darkmodeColor rounded-custom focus:ring-2 focus:ring-purple-500 transition-all"
                  />
                  {errors.pickupTiming && <p className="text-red-500 text-xs mt-1">{errors.pickupTiming.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 rounded-custom hover:opacity-90 transition-all flex items-center justify-center"
                >
                  {isSubmitting ? <Spinner /> : (
                    <>
                      <MdCheckCircle className="mr-2" />
                      Create Pickup Request
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Pickup Guidelines */}
          <div className="w-full md:w-1/2 overflow-y-auto custom-scrollbar dark:bg-darkmodeColor rounded-custom shadow-md dark:shadow-custom-dark p-8 text-gray-300 transform transition-all hover:scale-[1.02]">
            <div className="space-y-6">
              <div className="bg-[#233554] p-6 rounded-custom ">
                <h3 className="text-xl font-bold mb-4 text-white flex items-center">
                  <MdLocalShipping className="mr-3 text-purple-400" />
                  Pickup Guidelines
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <MdCheckCircle className="mr-2 text-green-400" />
                    Plan pickups at least 1 day in advance
                  </li>
                  <li className="flex items-center">
                    <MdCheckCircle className="mr-2 text-green-400" />
                    Select available warehouses only
                  </li>
                  <li className="flex items-center">
                    <MdCheckCircle className="mr-2 text-green-400" />
                    Confirm pickup details before submission
                  </li>
                </ul>
              </div>
              <div className="bg-[#233554] p-6 rounded-custom ">
                <h3 className="text-xl font-bold mb-4 text-white">Time Restrictions</h3>
                <p className="text-gray-400">
                  • No same-day pickups after 1 PM
                  • No pickups for past dates
                  • Time slots are subject to warehouse availability
                </p>
              </div>
              <div className="bg-[#233554] p-6 rounded-lg">
                <h3 className="text-sm font-bold text-white">
                  <span className="bg-purple-500 text-white p-1 rounded-custom"> Pickup automatically schedule by Carrier</span>
                </h3>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>

  );
}